import { ENABLE_PERMISSION_CHECK } from '../config/featureFlags';
import type { AccessRequirement, PermissionCode, RoleCode } from './types';

export function hasPermission(permissions: ReadonlySet<PermissionCode>, code: PermissionCode): boolean {
  if (!ENABLE_PERMISSION_CHECK) return true;
  return permissions.has(code);
}

export function hasAnyPermission(permissions: ReadonlySet<PermissionCode>, any: PermissionCode[] | undefined): boolean {
  if (!ENABLE_PERMISSION_CHECK) return true;
  if (!any || any.length === 0) return true;
  for (const p of any) if (permissions.has(p)) return true;
  return false;
}

export function hasAnyRole(roleCode: RoleCode, any: RoleCode[] | undefined): boolean {
  if (!ENABLE_PERMISSION_CHECK) return true;
  if (!any || any.length === 0) return true;
  return any.includes(roleCode);
}

/** Allocation officer = ALLOCATION_OFFICER role OR ALLOCATION_OFFICER_VIEW permission. */
export function isAllocationOfficer(
  roleCode: RoleCode,
  permissions: ReadonlySet<PermissionCode>,
): boolean {
  if (!ENABLE_PERMISSION_CHECK) return false;
  if (roleCode === 'ALLOCATION_OFFICER') return true;
  return permissions.has('ALLOCATION_OFFICER_VIEW');
}

/**
 * Single-purpose "officer view" permissions, each scoping a role to one specific
 * operational function. A role holding more than one of these is a combined/broader
 * role, not a narrow single-purpose one — see the bypass in canAccess below.
 */
const OFFICER_VIEW_PERMISSIONS: PermissionCode[] = [
  'ALLOCATION_OFFICER_VIEW',
  'ASSIGNED_OFFICER_VIEW',
  'SUPERVISOR_OFFICER_VIEW',
  'OFFICE_CLERK_VIEW',
  'MAILROOM_OFFICER_VIEW',
  'REC_MANAGEMENT_OFFICER_VIEW',
];

function countOfficerViewPermissions(permissions: ReadonlySet<PermissionCode>): number {
  let count = 0;
  for (const p of OFFICER_VIEW_PERMISSIONS) if (permissions.has(p)) count += 1;
  return count;
}

/**
 * Access rule: allow when role matches OR permission matches.
 * If no requirement is provided => allow.
 */
export function canAccess(req: AccessRequirement | undefined, input: { roleCode: RoleCode; permissions: ReadonlySet<PermissionCode> }): boolean {
  if (!ENABLE_PERMISSION_CHECK) return true;
  if (!req) return true;
  const byRole = req.anyRoleCodes?.length ? req.anyRoleCodes.includes(input.roleCode) : false;
  const byPerm = req.anyPermissions?.length ? hasAnyPermission(input.permissions, req.anyPermissions) : false;
  const allowed =
    req.anyRoleCodes?.length || req.anyPermissions?.length ? byRole || byPerm : true;
  if (!allowed) return false;
  if (req.denyAllocationOfficer && isAllocationOfficer(input.roleCode, input.permissions)) {
    // A role combining more than one officer-scoped view permission is broader than a
    // pure Allocation Officer — don't apply the exclusion to it.
    const bypass = countOfficerViewPermissions(input.permissions) > 1;
    if (!bypass) return false;
  }
  if (req.denyManagementAssistant && input.roleCode === 'OFFICE_CLERK') return false;
  return true;
}

